/**
 * Thin fetch wrapper for the Billable v2 API.
 *
 * ── Integration point with `apps/server` (Hono on Workers) ──────────────────
 * Base URL comes from `VITE_API_BASE_URL`. In dev, leaving it unset routes to
 * the in-repo mock API (see `mock/`), so the SPA runs standalone.
 *
 * All requests send `credentials: 'include'` because Better Auth issues an
 * httpOnly session cookie. There is deliberately no token handling here — if
 * the server ever moves to bearer tokens, this is the ONLY file that changes.
 */

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;

  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  get isAuth() {
    return this.status === 401 || this.status === 403;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Skip JSON parsing and hand back the raw Response (blobs, streams). */
  raw?: boolean;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions['query']) {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Listeners notified when the server says the session is gone. */
const unauthorizedHandlers = new Set<() => void>();
export function onUnauthorized(fn: () => void) {
  unauthorizedHandlers.add(fn);
  return () => unauthorizedHandlers.delete(fn);
}

export async function apiFetch(path: string, options: RequestOptions = {}) {
  const { method = 'GET', body, query, signal, headers } = options;

  const res = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  if (res.status === 401) {
    for (const fn of unauthorizedHandlers) fn();
  }

  return res;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await apiFetch(path, options);

  if (options.raw) {
    if (!res.ok) throw await toApiError(res);
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) throw await toApiError(res);

  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'invalid_response', 'Server returned malformed JSON.');
  }
}

/**
 * The server speaks two error dialects and this understands both.
 *
 *   { error: { code, message } }   the nested shape
 *   { error: "message", code? }    what `appError` emits, which is every
 *                                  route under /api/app
 *
 * Only the first was handled, so the flat one fell all the way through to
 * "Request failed (400)" and every message those routes carefully wrote — "This
 * invoice is already marked paid", "…has no valid email address" — was thrown
 * away in favor of a status code. Callers worked around it by reaching into
 * `ApiError.body` themselves, which is the symptom, not the fix.
 */
async function toApiError(res: Response) {
  let code = 'http_error';
  let message = `Request failed (${res.status})`;
  let body: unknown;
  try {
    body = await res.json();
    const b = body as {
      error?: string | { code?: string; message?: string };
      code?: string;
      message?: string;
    };

    if (typeof b?.error === 'string') {
      // Flat: the string IS the message, and any code sits beside it.
      message = b.error;
      if (b.code) code = b.code;
    } else if (b?.error) {
      if (b.error.code) code = b.error.code;
      if (b.error.message) message = b.error.message;
    } else if (b?.message) {
      message = b.message;
    }
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, code, message, body);
}

export const get = <T,>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
  api<T>(path, { query, signal });

export const post = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });

export const put = <T,>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });

export const patch = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body });

export const del = <T,>(path: string, body?: unknown) =>
  api<T>(path, { method: 'DELETE', body });
